import React, { ReactNode } from 'react';
import { useReportViewContext } from "@/app/features/reports/components/report-viewer-context";
import { cn } from "@/lib/utils";

// Helper function to escape special regex characters (using Lodash-like pattern)
const reRegExpChar = /[\\^$.*+?()[\]{}|]/g; // Match special characters

function escapeRegExp(string: string): string {
    // console.log('[escapeRegExp] Input:', string); // Removed log
    const result = string.replace(reRegExpChar, '\\$&'); // Prepend \ to matched characters
    // console.log('[escapeRegExp] Output:', result); // Removed log
    return result;
}

interface HighlightTextProps {
    children: ReactNode;
    className?: string;
}

const HighlightText: React.FC<HighlightTextProps> = ({ children, className }) => {
    const { filters: { filterInput } } = useReportViewContext();
    const isFilterActive = filterInput && filterInput.length > 0;

    const highlightNode = (node: ReactNode): ReactNode => {
        if (!isFilterActive) return node;

        if (typeof node === 'string') {
            const lines = (node as string)?.replace(/\\n/g, '\n');
            // Ensure filterInput exists before trying to escape and create regex
            if (!filterInput) return lines; 

            try {
                 // Escape the filter input before creating the RegExp
                const escapedFilterInput = escapeRegExp(filterInput);
                
                // --- DEBUG LOGGING START --- Removed logs
                // console.log('[HighlightText] filterInput:', filterInput);
                // console.log('[HighlightText] escapedFilterInput:', escapedFilterInput);
                // console.log('[HighlightText] Regex string:', `(${escapedFilterInput})`);
                // --- DEBUG LOGGING END ---

                const regex = new RegExp(`(${escapedFilterInput})`, 'gi');
                const parts = lines.split(regex);
                
                // Filter out empty strings that can result from splitting at the start/end
                const filteredParts = parts.filter(part => part !== '');

                return filteredParts.map((part, index) => 
                    // Use the original filterInput for case-insensitive comparison
                    part.toLowerCase() === filterInput.toLowerCase() 
                        ? <span key={index} className={cn("bg-yellow-300 py-0.5 px-1 rounded-md", className)}>{part}</span>
                        : part
                );
            } catch (error) {
                // Log the error and return the original text if regex fails
                console.error("Error creating or using RegExp for highlighting:", error);
                return lines; 
            }
        }

        // Handle React elements
        if (React.isValidElement(node)) {
            return React.cloneElement(node, {
                ...node.props,
                children: highlightNode(node.props.children)
            });
        }

        // Handle arrays of nodes
        if (Array.isArray(node)) {
            return node.map((child, index) => (
                <React.Fragment key={index}>
                    {highlightNode(child)}
                </React.Fragment>
            ));
        }

        // Return unchanged for other types
        return node;
    };

    return <>{highlightNode(children)}</>;
};

export default HighlightText;